import React, { ReactElement, useState } from 'react';
import { Team } from '@enview/interface/types/Team';
import MagnifyingGlass from '../../../components/svg/MagnifyingGlass';
import './WorkspaceSelectorContent.scss';

const NOT_YET_ADDED_MESSAGE =
  "This bill hasn't been added to any workspace yet. Choose one below:";

const matchesSearch = (workspace: Team, search: string): boolean => {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return workspace.name.toLowerCase().includes(query);
};

interface WorkspaceSelectorContentProps {
  /** The selectable options — every user workspace in assignment mode, or only the bill's existing workspaces when restricted */
  workspaces: Team[];
  /** Workspaces this bill already belongs to; empty means the bill isn't in a workspace yet */
  billWorkspaceIds: number[];
  /** Called with the selected workspace ids when Save is clicked. In restricted mode this is an action target, not a membership change. */
  onSave: (workspaceIds: number[]) => void;
  /** Heading shown above the workspace list, e.g. "APPLY PRIORITY IN..." */
  sectionLabel?: string;
  /** Disables Save while no workspace is selected, for callers where an empty selection isn't a valid outcome */
  requireSelection?: boolean;
  /** Disables Save while a previous save from this picker is still in flight, to prevent duplicate submissions */
  saving?: boolean;
  /**
   * When true, hides Select all / Deselect all so a caller scoping an action to a
   * bill's existing workspaces can't use this list to change bill workspace
   * membership. The Workspaces column (the only place membership is changed) never
   * sets this.
   */
  restricted?: boolean;
}

// The shared search + multi-select workspace list used by every workspace picker
// (the Workspaces column, and the workspace-scoping caret on Track/Priority/Workflow
// Status/Assignee). Callers own the popover shell (trigger + open/close state) and
// should only mount this while that popover is open, so its search/selection state
// starts fresh each time.
const WorkspaceSelectorContent = (
  props: WorkspaceSelectorContentProps,
): ReactElement => {
  const {
    workspaces,
    billWorkspaceIds,
    onSave,
    sectionLabel = 'WORKSPACES',
    requireSelection = false,
    saving = false,
    restricted = false,
  } = props;

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>(billWorkspaceIds);

  // billNotYetAdded only controls the helper text below — the selectable list is
  // always every workspace the user belongs to, so a bill already in one workspace
  // can still be added to another.
  const billNotYetAdded = billWorkspaceIds.length === 0;

  const filteredWorkspaces = workspaces.filter((workspace) =>
    matchesSearch(workspace, search),
  );

  const toggleWorkspace = (workspaceId: number): void => {
    setSelectedIds((prevSelectedIds) =>
      prevSelectedIds.includes(workspaceId)
        ? prevSelectedIds.filter((id) => id !== workspaceId)
        : [...prevSelectedIds, workspaceId],
    );
  };

  const handleSelectAll = (): void => {
    const filteredIds = filteredWorkspaces.map((workspace) => workspace.id);
    setSelectedIds((prevSelectedIds) =>
      Array.from(new Set([...prevSelectedIds, ...filteredIds])),
    );
  };

  const handleDeselectAll = (): void => {
    const filteredIds = filteredWorkspaces.map((workspace) => workspace.id);
    setSelectedIds((prevSelectedIds) =>
      prevSelectedIds.filter((id) => !filteredIds.includes(id)),
    );
  };

  const handleSave = (): void => {
    onSave(selectedIds);
  };

  return (
    <div className="workspace-picker">
      <div className="wp-search">
        <MagnifyingGlass height="14" width="14" />
        <input
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search workspaces..."
          type="text"
          value={search}
        />
      </div>

      <div className="wp-section-header">
        <span className="wp-section-label">{sectionLabel}</span>
        <span className="wp-selected-count">{selectedIds.length} selected</span>
      </div>

      {billNotYetAdded && <p className="wp-helper-text">{NOT_YET_ADDED_MESSAGE}</p>}

      <div className="wp-list">
        {filteredWorkspaces.map((workspace) => {
          const selected = selectedIds.includes(workspace.id);
          return (
            <label
              className={`wp-list-item ${selected ? 'selected' : ''}`}
              key={workspace.id}
            >
              <input
                checked={selected}
                disabled={saving}
                onChange={() => toggleWorkspace(workspace.id)}
                type="checkbox"
              />
              <span className="wp-name">{workspace.name}</span>
            </label>
          );
        })}

        {filteredWorkspaces.length === 0 && (
          <div className="wp-empty">No workspaces found</div>
        )}
      </div>

      {!restricted && (
        <div className="wp-select-actions">
          <button disabled={saving} onClick={handleSelectAll} type="button">
            Select all
          </button>
          <span className="wp-select-actions-divider">·</span>
          <button disabled={saving} onClick={handleDeselectAll} type="button">
            Deselect all
          </button>
        </div>
      )}

      <button
        className="wp-save"
        disabled={saving || (requireSelection && selectedIds.length === 0)}
        onClick={handleSave}
        type="button"
      >
        Save
      </button>
    </div>
  );
};

export default WorkspaceSelectorContent;
